import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useApiClient from "../hooks/useApiClient";
import useAuth from "../hooks/useAuth";
import { jwtDecode } from "jwt-decode";

function isTokenFresh(accessToken: string | undefined, bufferSec = 5): boolean {
  if (!accessToken) return false;
  try {
    const payload = jwtDecode<{ exp?: number }>(accessToken);
    return !!payload?.exp && payload.exp - Math.floor(Date.now() / 1000) > bufferSec;
  } catch {
    return false;
  }
}
import { ContacDetails } from "../types";
import { BasicMutationOptions } from "../queryClient";
import { CreateApplicationFormValuesType } from "../components/Form/schemas";
import { ProcedurePayload } from "./Tenant";
import { Parcel } from "../components/ParcelPreview/ParcelPreview";

export const useGetUserDataQuery = ({ tenantId, userId }: { tenantId: string; userId?: string }, enabled = true) => {
  const { privateApiClient } = useApiClient();

  return useQuery<UserDataResponse, AxiosError>({
    queryFn: async () => {
      return userId ? (await privateApiClient.get(`/tenant/${tenantId}/user/${userId}`)).data : null;
    },
    queryKey: ["user", tenantId, userId],
    enabled: enabled && !!userId,
  });
};

export const useEditUserMutation = (mutationOptions?: BasicMutationOptions<EditUserResponseData, AxiosError>) => {
  const { privateApiClient } = useApiClient();

  return useMutation<EditUserResponseData, AxiosError, EditUserRequestParams>({
    mutationFn: async ({ userId, tenantId, ...data }: EditUserRequestParams) =>
      (await privateApiClient.put(`/tenant/${tenantId}/user/${userId}`, data)).data,
    mutationKey: ["EditUser"],
    ...mutationOptions,
  });
};

export const useCreateApplicationMutation = (
  mutationOptions?: BasicMutationOptions<CreateApplicationResponseData, AxiosError>,
) => {
  const { publicApiClient, privateApiClient } = useApiClient();
  const { auth } = useAuth();

  return useMutation<CreateApplicationResponseData, AxiosError, CreateApplicationRequestParams>({
    mutationFn: async ({ formId, tenantId, files, ...data }: CreateApplicationRequestParams) => {
      const formData = new FormData();
      formData.append("payload", JSON.stringify({ email: data.applicants?.email, ...data }));
      files && Array.from(files).forEach((file) => formData.append("files", file, file.name));
      // Gość: publicApiClient (bez tokenu). Admin tej gminy ze świeżym tokenem:
      // privateApiClient — backend pozwala mu złożyć pismo testowe poza
      // terminem (tryb testowy), gościowi odmawia.
      const isAdminHere =
        !!auth.isLoggedIn &&
        (auth.isSuperadmin || (auth.isAdmin && auth.tenantId === tenantId)) &&
        isTokenFresh(auth.accessToken);
      const client = isAdminHere ? privateApiClient : publicApiClient;
      return (await client.post(`/tenant/${tenantId}/form/${formId}/application`, formData)).data;
    },
    mutationKey: ["CreateApplication"],
    ...mutationOptions,
  });
};

type CreateApplicationResponseData = any;
type CreateApplicationRequestParams = CreateApplicationFormValuesType &
  ProcedurePayload & {
    formId: string;
    tenantId: string;
  };

type UserPayload = {
  name: string;
  country: string;
  voivodeship: string;
  district: string;
  tenant: string;
  street: string;
  houseNumber: string;
  town: string;
  apartmentNumber?: string;
  postalCode: string;
  parcels: Parcel[];
};

type UserDataResponse = {
  id: string;
  email: string;
  payload: UserPayload;
} | null;
type EditUserRequestParams = {
  tenantId: string;
  userId: string;
  payload: ContacDetails;
};

type EditUserResponseData = {
  id: string;
  email: string;
  payload: UserPayload;
};
